"use client";
import { getApiBaseUrl } from "@/lib/apiBase";
import { fetchJsonArray } from "@/lib/fetchJsonSafe";

import { useState, useEffect, useMemo } from "react";
import Image from "next/image";
import Slider from "react-slick";
import Link from "next/link";
import { ArrowRight } from "lucide-react";

const BASE_URL = getApiBaseUrl();

/** API баннер ирэхгүй үед харуулах өргөн, товч өндөрт тохирсон зураг */
const FALLBACK_BANNER_IMAGE =
  "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=2000&q=85";

function bannerImageSrc(banner) {
  const raw = banner?.banner_image;
  if (!raw) return null;
  if (String(raw).startsWith("http")) return raw;
  const path = String(raw).startsWith("/") ? raw : `/${raw}`;
  return BASE_URL ? `${BASE_URL.replace(/\/+$/, "")}${path}` : path;
}

function shouldUnoptimizeImage(src) {
  return typeof src === "string" && /^data:/i.test(src);
}

function FallbackHorizontalBanner() {
  return (
    <section className="mx-auto mb-10 max-w-[1440px] px-4">
      <Link
        href="/partner/partnersearch?search=&district="
        className="group relative block h-[168px] w-full overflow-hidden rounded-2xl shadow-lg ring-1 ring-slate-200/80 transition hover:shadow-xl hover:ring-[#577099]/25 md:h-[200px]"
      >
        <Image
          src={FALLBACK_BANNER_IMAGE}
          alt="Бизнес, хамтын ажиллагаа"
          fill
          className="object-cover transition duration-700 group-hover:scale-[1.02]"
          sizes="(max-width: 1440px) 100vw, 1440px"
          priority={false}
          unoptimized={false}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1e293b]/88 via-[#1e293b]/55 to-[#577099]/35" />
        <div className="absolute inset-0 flex flex-col justify-center px-6 py-4 md:flex-row md:items-center md:justify-between md:px-10 md:py-0">
          <div className="max-w-xl text-white">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/80">
              Chanaraas
            </p>
            <h2 className="mt-1 text-xl font-bold leading-tight drop-shadow-md md:text-2xl lg:text-3xl">
              Хамтрагч байгууллагаа олж, үйлчилгээгээ өргөжүүлээрэй
            </h2>
            <p className="mt-2 hidden text-sm text-white/85 sm:block md:text-base">
              Мянга мянган компани, сэтгэгдэл, арга хэмжээ — нэг платформ дээр.
            </p>
          </div>
          <span className="mt-3 inline-flex items-center gap-2 self-start rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#3d5280] shadow-md transition group-hover:bg-[#577099] group-hover:text-white md:mt-0 md:self-center">
            Хайлт үзэх
            <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
          </span>
        </div>
      </Link>
    </section>
  );
}

export default function HorizontalBanner() {
  const [bannersHomeHorizontal, setBannersHomeHorizontal] = useState([]);
  const [loading, setLoading] = useState(true);

  const settings = useMemo(
    () => ({
      dots: true,
      infinite: true,
      speed: 800,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 4500,
      arrows: true,
      appendDots: (dots) => (
        <div
          style={{ position: "absolute", bottom: "12px", width: "100%" }}
          className="flex justify-center"
        >
          <ul className="m-0 flex list-none justify-center gap-2 p-0">{dots}</ul>
        </div>
      ),
      customPaging: () => (
        <div className="h-2.5 w-2.5 rounded-full bg-white/60 transition hover:bg-white" />
      ),
    }),
    []
  );

  useEffect(() => {
    const fetchBannersHomeHorizontal = async () => {
      try {
        setLoading(true);
        const data = await fetchJsonArray(`/api/banner?type=banner&bannertypeid=4`);
        setBannersHomeHorizontal(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching horizontal banners:", error);
        setBannersHomeHorizontal([]);
      } finally {
        setLoading(false);
      }
    };
    fetchBannersHomeHorizontal();
  }, []);

  if (loading) {
    return (
      <div className="mx-auto mb-10 max-w-[1440px] px-4">
        <div className="relative h-[168px] w-full animate-pulse overflow-hidden rounded-2xl bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 md:h-[200px]" />
      </div>
    );
  }

  if (bannersHomeHorizontal.length === 0) {
    return <FallbackHorizontalBanner />;
  }

  return (
    <div className="mx-auto mb-10 max-w-[1440px] px-4">
      <div className="relative h-[168px] w-full overflow-hidden rounded-2xl shadow-lg ring-1 ring-slate-200/80 md:h-[200px]">
        <Slider {...settings}>
          {bannersHomeHorizontal.map((banner) => {
            const src = bannerImageSrc(banner);
            const href = banner.banner_link || "#";
            return (
              <div key={banner.id} className="relative h-[168px] md:h-[200px]">
                <Link href={href} target="_blank" rel="noopener noreferrer" className="relative block h-full w-full">
                  {src ? (
                    <Image
                      src={src}
                      alt={banner.banner_title || "Баннер"}
                      fill
                      className="object-cover"
                      priority
                      unoptimized={shouldUnoptimizeImage(src)}
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-[#577099] to-[#3d5280]" />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/25">
                    {banner.banner_title && (
                      <h2 className="px-4 text-center text-xl font-bold text-white drop-shadow-lg md:text-3xl lg:text-4xl">
                        {banner.banner_title}
                      </h2>
                    )}
                  </div>
                </Link>
              </div>
            );
          })}
        </Slider>
      </div>
    </div>
  );
}
